import React, { FC, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Confessions.css";

const Confessions: FC = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [campus, setCampus] = useState("trafalgar");
  const [category, setCategory] = useState("school-life");
  const [confessions, setConfessions] = useState<any[]>([]);
  const [replyContent, setReplyContent] = useState<{ [key: number]: string }>(
    {},
  );
  const [replies, setReplies] = useState<{ [key: number]: any[] }>({});
  const [showReplyBox, setShowReplyBox] = useState<{ [key: number]: boolean }>(
    {},
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await fetch("https://api.deceit.dev/confessions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, content: body, campus, category }),
    });

    if (res.ok) {
      toast.success("Confession submitted successfully!");
      fetchConfessions();
      setTitle("");
      setBody("");
      setCampus("Trafalgar");
      setCategory("School-Life");
    } else {
      toast.error("Failed to submit confession. Please try again.");
    }
  };

  const fetchConfessions = async () => {
    const res = await fetch("https://api.deceit.dev/confessions");
    const data = await res.json();
    setConfessions(data);
    data.forEach((conf: any) => fetchReplies(conf.id));
  };

  const fetchReplies = async (confessionId: number) => {
    const res = await fetch(
      `https://api.deceit.dev/confessions/${confessionId}/replies`,
    );
    const data = await res.json();
    setReplies((prev) => ({ ...prev, [confessionId]: data }));
  };

  const submitReply = async (confessionId: number) => {
    const content = replyContent[confessionId];
    if (!content) return;

    const res = await fetch("https://api.deceit.dev/replies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confession_id: confessionId, content }),
    });

    if (res.ok) {
      toast.success("Reply posted");
      fetchReplies(confessionId);
      setReplyContent((prev) => ({ ...prev, [confessionId]: "" }));
      setShowReplyBox((prev) => ({ ...prev, [confessionId]: false }));
    } else {
      toast.error("Failed to post reply");
    }
  };

  useEffect(() => {
    fetchConfessions();
  }, []);

  return (
    <div className="text-white">
      <h1 className="text-4xl font-bold">Submit a Confession</h1>
      <div className="text-gray-300 my-2 italic">
        Your posts may be anonymous, but that does not mean you are.
        <br />
        Keep it respectful, this chat is moderated. Threats, spam, and bullying
        will be removed or reported.
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 mb-6">
        <input
          type="text"
          placeholder="Confession Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-2 border border-blue-900 bg-blue-950 rounded-sm"
          required
        />
        <textarea
          placeholder="Write your confession..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full px-4 py-2 border border-blue-900 bg-blue-950 rounded-sm h-40"
          required
        />
        <div className="flex gap-4">
          <select
            value={campus}
            onChange={(e) => setCampus(e.target.value)}
            className="px-4 py-2 border border-blue-900 bg-blue-950 rounded-sm"
          >
            <option value="Trafalgar">Trafalgar</option>
            <option value="Davis">Davis</option>
            <option value="HMC">HMC</option>
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 border border-blue-900 bg-blue-950 rounded-sm"
          >
            <option value="School-Life">School Life</option>
            <option value="Mental-Health">Mental Health</option>
            <option value="Relationships">Relationships</option>
            <option value="Funny">Funny</option>
            <option value="Vent">Vent</option>
            <option value="Random">Random</option>
          </select>
        </div>
        <button
          type="submit"
          className="bg-green-400 hover:bg-green-300 text-slate-950 font-bold px-6 py-2 rounded-sm"
        >
          Submit Confession
        </button>
      </form>

      <h1 className="text-4xl font-bold mb-4">Recent Confessions</h1>
      {confessions.map((conf) => (
        <div
          key={conf.id}
          className="bg-blue-950 p-4 pb-4 border border-blue-900 rounded-sm shadow-md mb-6"
        >
          <h3 className="text-2xl font-bold text-gray-100 mb-1">
            {conf.title}
          </h3>
          <div className="text-sm text-blue-300 mb-2">Anonymous Student</div>
          <p className="text-gray-300 mb-2">{conf.content}</p>
          <div className="text-sm text-blue-300 mb-2">
            {conf.campus.charAt(0).toUpperCase() + conf.campus.slice(1)} •{" "}
            {conf.category?.replace("-", " ") ?? "Uncategorized"} •{" "}
            {new Date(conf.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>

          <div className="flex items-center gap-4 text-blue-300 text-sm mb-2">
            <button
              onClick={() =>
                setShowReplyBox((prev) => ({
                  ...prev,
                  [conf.id]: !prev[conf.id],
                }))
              }
              className="hover:text-white"
            >
              <FontAwesomeIcon icon={faComment} /> Reply
            </button>
          </div>

          {showReplyBox[conf.id] && (
            <div className="mb-4">
              <textarea
                className="w-full px-4 py-2 border border-blue-900 bg-blue-950 rounded-sm h-24 text-white mb-2"
                placeholder="Write a reply..."
                value={replyContent[conf.id] || ""}
                onChange={(e) =>
                  setReplyContent((prev) => ({
                    ...prev,
                    [conf.id]: e.target.value,
                  }))
                }
              />
              <button
                onClick={() => submitReply(conf.id)}
                className="bg-blue-500 hover:bg-blue-400 text-white px-4 pt-1 pb-0 rounded-sm"
              >
                Post Reply
              </button>
            </div>
          )}

          {replies[conf.id] && replies[conf.id].length > 0 && (
            <div className="mt-4 border-t border-blue-900 pt-2 text-sm space-y-2 text-blue-200">
              {replies[conf.id].map((reply) => (
                <div key={reply.id} className="bg-blue-900 p-2 rounded-sm">
                  <p className="text-gray-300">{reply.content}</p>
                  <div className="text-xs text-blue-400 mt-1">
                    {new Date(reply.created_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <ToastContainer />
    </div>
  );
};

export default Confessions;
